import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { contentAPI } from '../services/api'
import type { RecapAutoDraftPlatform, RecapPlatform } from '../types'
import { apiErrorMessage } from '../utils/apiError'
import {
  formatCentralTime,
  toTwelveHour,
  toTwentyFourHour,
  type Period,
} from '../utils/centralTime'

const PLATFORM_NAMES: Record<RecapPlatform, string> = {
  twitter: 'Twitter',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  blog: 'Blog',
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1)
// The beat job runs every 15 minutes, so quarter hours are what it can honour.
const MINUTES = [0, 15, 30, 45]

/**
 * Admin only: per-platform auto-drafting of the house daily recap, at a
 * Central time. The drafts are saved for review; nothing is published.
 */
const RecapAutoDraftPanel = () => {
  const [platforms, setPlatforms] = useState<RecapAutoDraftPlatform[] | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const settings = await contentAPI.getRecapAutoDraft()
        setPlatforms(settings.platforms)
      } catch {
        setLoadFailed(true)
      }
    }
    load()
  }, [])

  const update = (platform: RecapPlatform, change: Partial<RecapAutoDraftPlatform>) =>
    setPlatforms((prev) =>
      (prev ?? []).map((p) => (p.platform === platform ? { ...p, ...change } : p))
    )

  const setTime = (
    setting: RecapAutoDraftPlatform,
    change: Partial<{ hour: number; minute: number; period: Period }>
  ) =>
    update(setting.platform, {
      draft_time: toTwentyFourHour({ ...toTwelveHour(setting.draft_time), ...change }),
    })

  const handleSave = async () => {
    if (!platforms) return
    setSaving(true)
    try {
      const saved = await contentAPI.saveRecapAutoDraft(platforms)
      setPlatforms(saved.platforms)
      toast.success('Auto-draft settings saved')
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save auto-draft settings'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="recap-autodraft-panel">
      <h3 className="text-xl font-bold text-gray-900">Auto-draft daily recap</h3>
      <p className="text-sm text-gray-600 mt-1">
        Each day at the time you pick (Central time), a recap of that Central day's signal
        closes is drafted for each platform turned on. Drafts only: nothing is published. A day
        with no closes, or one that already has a recap, gets no draft.
      </p>

      {loadFailed && (
        <p role="alert" className="text-sm text-danger-600 mt-3">
          Couldn't load the auto-draft settings.
        </p>
      )}

      {platforms && (
        <>
          <ul className="mt-4 divide-y divide-gray-100">
            {platforms.map((setting) => {
              const name = PLATFORM_NAMES[setting.platform]
              const { hour, minute, period } = toTwelveHour(setting.draft_time)
              const minutes = MINUTES.includes(minute) ? MINUTES : [...MINUTES, minute].sort(
                (a, b) => a - b
              )
              return (
                <li
                  key={setting.platform}
                  className="flex flex-wrap items-center justify-between gap-3 py-3"
                >
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      role="switch"
                      aria-label={`Auto-draft ${name}`}
                      checked={setting.enabled}
                      onChange={(e) => update(setting.platform, { enabled: e.target.checked })}
                      className="h-4 w-4"
                    />
                    {name}
                  </label>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <select
                      aria-label={`${name} hour`}
                      value={hour}
                      onChange={(e) => setTime(setting, { hour: Number(e.target.value) })}
                      className="px-2 py-1 border border-gray-300 rounded-md"
                    >
                      {HOURS.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                    :
                    <select
                      aria-label={`${name} minute`}
                      value={minute}
                      onChange={(e) => setTime(setting, { minute: Number(e.target.value) })}
                      className="px-2 py-1 border border-gray-300 rounded-md"
                    >
                      {minutes.map((m) => (
                        <option key={m} value={m}>
                          {String(m).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <select
                      aria-label={`${name} AM or PM`}
                      value={period}
                      onChange={(e) => setTime(setting, { period: e.target.value as Period })}
                      className="px-2 py-1 border border-gray-300 rounded-md"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <span>CT</span>
                    <span
                      className="ml-2 text-gray-500"
                      data-testid={`recap-autodraft-${setting.platform}-summary`}
                    >
                      {setting.enabled
                        ? `Drafts daily at ${formatCentralTime(setting.draft_time)}`
                        : 'Off'}
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
          <button
            onClick={handleSave}
            disabled={saving}
            className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save auto-draft settings'}
          </button>
        </>
      )}
    </div>
  )
}

export default RecapAutoDraftPanel
